import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, CheckCircle2, ClipboardPlus, Filter, ShieldAlert } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { getFacilities, type FacilityOption } from '../../lib/services/facilities'
import {
  createQapiFindingPip,
  listQapiFindings,
  updateQapiFindingStatus,
  type QapiFinding,
  type QapiFindingSeverity,
  type QapiFindingStatus,
} from '../../lib/services/chartAudit'

const STATUS_OPTIONS: Array<{ value: QapiFindingStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_review', label: 'In Review' },
  { value: 'pip_created', label: 'PIP Created' },
  { value: 'resolved', label: 'Resolved' },
]

const MANAGEABLE_STATUSES: QapiFindingStatus[] = ['open', 'in_review', 'resolved']

const STATUS_STYLES: Record<QapiFindingStatus, string> = {
  open: 'bg-rose-50 text-rose-700',
  in_review: 'bg-amber-50 text-amber-700',
  pip_created: 'bg-blue-50 text-blue-700',
  resolved: 'bg-emerald-50 text-emerald-700',
}

const SEVERITY_STYLES: Record<QapiFindingSeverity, string> = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-amber-50 text-amber-700',
  high: 'bg-rose-50 text-rose-700',
}

function normalizeDate(value: string | null): string {
  if (!value) return 'Not recorded'
  return new Date(value).toLocaleDateString()
}

export function ChartAuditQapiPage() {
  const { viewerContext } = useAuth()
  const canManageFindings = viewerContext?.role === 'Admin' || viewerContext?.is_superadmin === true

  const [facilities, setFacilities] = useState<FacilityOption[]>([])
  const [selectedFacilityId, setSelectedFacilityId] = useState('all')
  const [selectedStatus, setSelectedStatus] = useState<QapiFindingStatus | 'all'>('all')
  const [findings, setFindings] = useState<QapiFinding[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [busyFindingId, setBusyFindingId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function loadFacilities() {
      try {
        const rows = await getFacilities()
        if (!cancelled) setFacilities(rows)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load facilities')
      }
    }

    loadFacilities()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function loadFindings() {
      try {
        setLoading(true)
        setError(null)
        const rows = await listQapiFindings({
          facilityId: selectedFacilityId === 'all' ? undefined : selectedFacilityId,
          status: selectedStatus,
        })
        if (!cancelled) setFindings(rows)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load QAPI findings')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadFindings()
    return () => { cancelled = true }
  }, [selectedFacilityId, selectedStatus])

  const facilityMap = useMemo(
    () => new Map(facilities.map((facility) => [facility.facility_id, facility.facility_name])),
    [facilities],
  )

  const openCount = findings.filter((finding) => finding.status === 'open').length
  const highSeverityCount = findings.filter((finding) => finding.severity === 'high').length
  const pipCount = findings.filter((finding) => finding.status === 'pip_created').length

  async function handleStatusChange(findingId: string, status: QapiFindingStatus) {
    setBusyFindingId(findingId)
    setMessage(null)
    setError(null)
    try {
      await updateQapiFindingStatus(findingId, status)
      setFindings((current) => current.map((finding) => (
        finding.id === findingId
          ? {
              ...finding,
              status,
              resolved_at: status === 'resolved' ? new Date().toISOString() : finding.resolved_at,
            }
          : finding
      )))
      setMessage('QAPI finding updated.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update QAPI finding')
    } finally {
      setBusyFindingId(null)
    }
  }

  async function handleCreatePip(findingId: string) {
    setBusyFindingId(findingId)
    setMessage(null)
    setError(null)
    try {
      const pip = await createQapiFindingPip(findingId)
      setFindings((current) => current.map((finding) => (
        finding.id === findingId
          ? {
              ...finding,
              status: 'pip_created',
              pip_action_id: pip.pip_action_id,
            }
          : finding
      )))
      setMessage('PIP corrective action created.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create PIP')
    } finally {
      setBusyFindingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="v11-workspace-header flex items-center gap-4">
        <Link to="/chart-audit" className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
          <ArrowLeft size={18} />
        </Link>
        <div className="flex-1">
          <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">Chart Audit / QAPI</p>
          <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Chart Audit QAPI</h1>
          <p className="mt-1 text-sm text-slate-500">
            Tenant-scoped QAPI findings generated from chart-audit batches, with guarded admin follow-up actions.
          </p>
        </div>
      </div>

      <div className="v11-kpi-grid">
        <div className="v11-kpi-card">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Visible Findings</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{findings.length}</p>
        </div>
        <div className="v11-kpi-card v11-kpi-accent-rose">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Open</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{openCount}</p>
        </div>
        <div className="v11-kpi-card v11-kpi-accent-gold">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">High Severity / PIPs</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{highSeverityCount} / {pipCount}</p>
        </div>
      </div>

      <div className="v11-section flex flex-wrap items-end gap-3 p-4">
        <div className="min-w-[220px] flex-1">
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
            Facility
            <select
              value={selectedFacilityId}
              onChange={(event) => setSelectedFacilityId(event.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
            >
              <option value="all">All Facilities</option>
              {facilities.map((facility) => (
                <option key={facility.facility_id} value={facility.facility_id}>{facility.facility_name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="min-w-[220px] flex-1">
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
            Status
            <select
              value={selectedStatus}
              onChange={(event) => setSelectedStatus(event.target.value as QapiFindingStatus | 'all')}
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="inline-flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500">
          <Filter size={14} className="text-slate-400" />
          {canManageFindings ? 'Admins can update statuses and create PIPs.' : 'Read-only view for non-admin users.'}
        </div>
      </div>

      {message ? <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">{message}</div> : null}
      {error ? <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div> : null}

      {loading ? (
        <div className="py-12 text-center text-sm text-slate-500">Loading QAPI findings...</div>
      ) : findings.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center text-sm text-slate-500">
          No QAPI findings match the current filters.
        </div>
      ) : (
        <div className="space-y-3">
          {findings.map((finding) => (
            <div key={finding.id} className="v11-dashboard-card p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                      {facilityMap.get(finding.facility_id) ?? 'Unknown facility'}
                    </span>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[finding.status]}`}>
                      {finding.status.replace('_', ' ')}
                    </span>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${SEVERITY_STYLES[finding.severity]}`}>
                      {finding.severity} severity
                    </span>
                    {finding.scope_level ? (
                      <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
                        {finding.scope_level}
                      </span>
                    ) : null}
                  </div>

                  <h2 className="mt-3 text-lg font-bold text-slate-900">{finding.standard}</h2>
                  <p className="mt-1 text-sm font-semibold text-slate-600">{finding.domain}</p>
                  <p className="mt-2 text-sm text-slate-700">{finding.finding_description}</p>

                  <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
                    <span>Created {normalizeDate(finding.created_at)}</span>
                    <span>Batch Date {normalizeDate(finding.batch_date)}</span>
                    <span>Chart refs {finding.chart_token_refs.length}</span>
                    {finding.resolved_at ? <span>Resolved {normalizeDate(finding.resolved_at)}</span> : null}
                  </div>
                </div>

                <div className="min-w-[220px] space-y-3">
                  {canManageFindings ? (
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Update Status
                      <select
                        value={finding.status}
                        onChange={(event) => handleStatusChange(finding.id, event.target.value as QapiFindingStatus)}
                        disabled={busyFindingId === finding.id}
                        className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 disabled:opacity-50"
                      >
                        {MANAGEABLE_STATUSES.map((status) => (
                          <option key={status} value={status}>{status.replace('_', ' ')}</option>
                        ))}
                        {finding.status === 'pip_created' ? <option value="pip_created">pip created</option> : null}
                      </select>
                    </label>
                  ) : (
                    <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                      <div className="flex items-center gap-2">
                        <ShieldAlert size={14} className="text-slate-400" />
                        Admin role required to change QAPI statuses.
                      </div>
                    </div>
                  )}

                  {canManageFindings && finding.status !== 'pip_created' ? (
                    <button
                      type="button"
                      onClick={() => handleCreatePip(finding.id)}
                      disabled={busyFindingId === finding.id}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <ClipboardPlus size={16} />
                      {busyFindingId === finding.id ? 'Working...' : 'Create PIP'}
                    </button>
                  ) : null}

                  {finding.pip_action_id ? (
                    <div className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 size={14} />
                        PIP linked to corrective action {finding.pip_action_id.slice(0, 8)}...
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!canManageFindings ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-800">
          <div className="flex items-start gap-2">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <p>
              This workspace is intentionally read-only for non-admin users. Status changes and PIP creation stay behind the
              governed backend role checks added in PR #814.
            </p>
          </div>
        </div>
      ) : null}
    </div>
  )
}
